import { useAtom } from "jotai";

import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import type { NumberField } from "@frontend/lib/form";
import { Trans, useLingui } from "@lingui/react/macro";

import type { LocationTypedAddress } from "../types";

export type AddressFieldProps = {
  field: NumberField<number | null | undefined>;
  addresses: LocationTypedAddress[];
  disabled?: boolean;
  error?: string | null;
  onAddressSelected?: (addressId: number | null) => void;
};

export function AddressField({
  field,
  addresses,
  disabled,
  error,
  onAddressSelected,
}: AddressFieldProps) {
  const { t } = useLingui();

  const [addressId, setAddressId] = useAtom(field.atom);

  const items = addresses.map((address) => ({
    value: address.id,
    label: address.displayAddress || address.place || `#${address.id}`,
  }));

  return (
    <Field invalid={Boolean(error)}>
      <FieldLabel>
        <Trans>Existing address</Trans>
      </FieldLabel>
      <FieldContent>
        <Select
          items={items}
          value={addressId ?? null}
          onValueChange={(value) => {
            setAddressId(value ?? undefined);
            onAddressSelected?.(value ?? null);
          }}
          disabled={disabled || items.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder={t`Choose an address`} />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError match={Boolean(error)}>{error}</FieldError>
      </FieldContent>
    </Field>
  );
}
